import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useAuth from "../../auth/accessToken/hooks/useAuth";
import { isPartner } from "../../auth/helpers";
import { getRecentAccountIds } from "../../auth/recentAccounts";
import { useAccountId } from "../../auth/useAccountId";
import MerchantLogo from "../../components/AppBar/MerchantLogo";
import Icon from "../../components/Icons";
import { P } from "../../components/Typography";
import View from "../../components/View";
import {
    defaultRadius,
    distances,
    focusBoxShadow,
    palette,
    placeholderColor,
    shadows,
} from "../../styles/constants";
import type { AuthenticatedAccountUser } from "../../types/management-auth";
import { isRootAccount } from "../../util/accountId";
import { isMobile } from "../../util/dom";

type AccountListProps = {
    currentAccount?: AuthenticatedAccountUser["accounts"][number] | undefined;
    onChangeAccount: (
        account: AuthenticatedAccountUser["accounts"][number],
    ) => void;
};

type ProcessedAccount = AuthenticatedAccountUser["accounts"][number] & {
    testAccount?: AuthenticatedAccountUser["accounts"][number];
    productionAccount?: AuthenticatedAccountUser["accounts"][number];
    originalAccountId: string;
};

const processAccounts = (
    accounts: AuthenticatedAccountUser["accounts"],
    accountId: string,
): ProcessedAccount[] => {
    const internalAccountId = accountId.substring(1);
    const reducedAccounts = accounts.reduce<ProcessedAccount[]>(
        (acc, account) => {
            const baseId = account.account_id.substring(1);
            const productionAccountId = `P${baseId}`;
            const testAccountId = `T${baseId}`;

            const alreadyProcessed = acc.some(
                (processedAccount) => processedAccount.account_id === baseId,
            );
            if (alreadyProcessed) {
                return acc;
            }

            const productionAccount = accounts.find(
                (account) => account.account_id === productionAccountId,
            );
            const testAccount = accounts.find(
                (a) => a.account_id === testAccountId,
            );

            acc.push({
                ...account,
                ...(productionAccount && { productionAccount }),
                ...(testAccount && { testAccount }),
                account_id: baseId,
                originalAccountId: account.account_id,
            });

            return acc;
        },
        [],
    );
    return reducedAccounts.filter(
        (account) => account.account_id !== internalAccountId,
    );
};

const gotoAccount = (
    isTestMode: boolean,
    accountId: string,
    account: ProcessedAccount,
    onChangeAccount: (
        account: AuthenticatedAccountUser["accounts"][number],
    ) => void,
) => {
    const targetAccount = isTestMode
        ? account.testAccount
        : account.productionAccount;
    const fallbackAccount = {
        ...account,
        account_id: account.originalAccountId,
    };
    // If neither test nor production account exists, try switching to the other mode's account_id
    if (!targetAccount) {
        fallbackAccount.account_id = isTestMode
            ? `P${account.account_id}`
            : `T${account.account_id}`;
    }
    onChangeAccount(targetAccount || fallbackAccount);
};

export default function AccountList({
    currentAccount,
    onChangeAccount,
}: AccountListProps) {
    const shouldAutoFocus = !isMobile();
    const { t } = useTranslation();
    const accountId = useAccountId();
    const [query, setQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    // Only keyboard navigation scrolls, scrolling on hover moves the account
    // away from the pointer before it is clicked
    const [scrollToHighlighted, setScrollToHighlighted] = useState(false);
    const listRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const showOrgNumber =
        isRootAccount(currentAccount?.account_id) ||
        isPartner(currentAccount?.account_id || "");

    const accountUser = useAuth((state) => state.account_user);
    const allAccountsRaw = useMemo(() => {
        return processAccounts(accountUser.accounts, accountId);
    }, [accountUser.accounts, accountId]);
    // Read when the list opens, the app records each account it shows. Looked
    // up in the current accounts so names and logos are up to date, and so
    // the current account (left out of allAccountsRaw) is skipped.
    const [recentAccountIds] = useState(() => getRecentAccountIds(accountUser));
    const recentAccountsState = recentAccountIds.flatMap(
        (id) =>
            allAccountsRaw.find((account) => account.account_id === id) ?? [],
    );
    const allAccounts = allAccountsRaw
        .filter((account) => {
            const searchTerm = query.toLowerCase();
            return [
                account.display_name,
                account.business_name,
                account.account_id,
                account.testAccount?.account_id,
                account.productionAccount?.account_id,
            ].some((field) => field?.toLowerCase().includes(searchTerm));
        })
        .filter(
            (account) =>
                !recentAccountsState.some(
                    (recent) => recent.account_id === account.account_id,
                ),
        );
    const recentAccounts = recentAccountsState.filter((account) => {
        const searchTerm = query.toLowerCase();
        return [
            account.display_name,
            account.business_name,
            account.account_id,
            account.testAccount?.account_id,
            account.productionAccount?.account_id,
        ].some((field) => field?.toLowerCase().includes(searchTerm));
    });

    const selectableAccounts = useMemo(() => {
        return [...recentAccounts, ...allAccounts];
    }, [recentAccounts, allAccounts]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Keep the function memoized
    const handleChangeAccount = useCallback(
        (account: ProcessedAccount) => () => {
            // Recent accounts are recorded when the app shows the account
            if (query) {
                return gotoAccount(
                    query.startsWith("T"),
                    accountId,
                    account,
                    onChangeAccount,
                );
            }
            gotoAccount(
                accountId.startsWith("T"),
                accountId,
                account,
                onChangeAccount,
            );
        },
        [query],
    );

    useEffect(() => {
        if (query) {
            setScrollToHighlighted(true);
            setHighlightedIndex(0);
        }
    }, [query]);

    const highlightWithMouse = (index: number) => {
        setScrollToHighlighted(false);
        setHighlightedIndex(index);
    };

    const handleSearchInputKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setScrollToHighlighted(true);
            setHighlightedIndex((prev) =>
                Math.min(prev + 1, selectableAccounts.length - 1),
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setScrollToHighlighted(true);
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const selectedAccount = selectableAccounts[highlightedIndex];
            if (selectedAccount) {
                handleChangeAccount(selectedAccount)();
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setQuery("");
            setScrollToHighlighted(true);
            setHighlightedIndex(0);
        }
    };

    if (recentAccounts.length === 0 && allAccounts.length === 0 && !query) {
        // No other accounts to display
        return null;
    }

    return (
        <AccountListElement>
            <SearchInputElement
                ref={inputRef}
                onChange={(e) => setQuery(e.target.value)}
                value={query}
                placeholder={t("change_account.search_accounts_placeholder")}
                autoFocus={shouldAutoFocus}
                role="combobox"
                aria-expanded={selectableAccounts.length > 0}
                aria-haspopup="listbox"
                aria-controls="account-list"
                aria-activedescendant={
                    selectableAccounts[highlightedIndex]
                        ? `account-${selectableAccounts[highlightedIndex].account_id}`
                        : undefined
                }
                onKeyDown={handleSearchInputKeyDown}
            />
            <ListElement role="listbox" id="account-list" ref={listRef}>
                {recentAccounts.length > 0 && (
                    <>
                        <StickyListHeaderElement role="listitem">
                            {t("change_account.recently_used")}
                        </StickyListHeaderElement>
                        {recentAccounts.map((account, index) => (
                            <ListItem
                                key={account.account_id}
                                onClick={handleChangeAccount(account)}
                                as="button"
                                role="option"
                                aria-selected={index === highlightedIndex}
                                $highlighted={index === highlightedIndex}
                                $scrollIntoView={scrollToHighlighted}
                                onMouseEnter={() => highlightWithMouse(index)}
                                id={`account-${account.account_id}`}
                            >
                                <MerchantLogo
                                    src={account.icon_url}
                                    alt={
                                        account.display_name ||
                                        account.business_name ||
                                        account.account_id
                                    }
                                />
                                <section>
                                    <P
                                        variant="sm"
                                        fontWeight={600}
                                        color={palette.white}
                                    >
                                        {account.display_name ||
                                            account.public_business_name ||
                                            account.business_name}
                                        {showOrgNumber && (
                                            <>
                                                {" "}
                                                <P
                                                    variant="sm"
                                                    fontWeight={400}
                                                    color={palette.neutral[300]}
                                                    as="strong"
                                                >
                                                    -
                                                </P>{" "}
                                                <P
                                                    variant="sm"
                                                    fontWeight={400}
                                                    color={palette.neutral[300]}
                                                    as="strong"
                                                >
                                                    {
                                                        account.organization_number
                                                    }
                                                </P>
                                            </>
                                        )}
                                    </P>
                                    <P
                                        variant="xs"
                                        color={palette.neutral[300]}
                                        fontWeight={500}
                                    >
                                        {account.account_id}
                                    </P>
                                </section>
                                <Icon icon="switch_horizontal" size="small" />
                            </ListItem>
                        ))}
                    </>
                )}
                <StickyListHeaderElement role="listitem">
                    {t("change_account.accounts")}
                </StickyListHeaderElement>
                {allAccounts.map((account, index) => {
                    const actualIndex = recentAccounts.length + index;
                    return (
                        <ListItem
                            key={account.account_id}
                            onClick={handleChangeAccount(account)}
                            as="button"
                            role="option"
                            aria-selected={actualIndex === highlightedIndex}
                            $highlighted={actualIndex === highlightedIndex}
                            $scrollIntoView={scrollToHighlighted}
                            onMouseEnter={() => highlightWithMouse(actualIndex)}
                            id={`account-${account.account_id}`}
                        >
                            <MerchantLogo
                                src={account.icon_url}
                                alt={
                                    account.display_name ||
                                    account.business_name ||
                                    account.account_id
                                }
                            />
                            <section>
                                <P
                                    variant="sm"
                                    fontWeight={600}
                                    color={palette.white}
                                >
                                    {account.display_name ||
                                        account.public_business_name ||
                                        account.business_name}
                                    {showOrgNumber && (
                                        <>
                                            {" "}
                                            <P
                                                variant="sm"
                                                fontWeight={400}
                                                color={palette.neutral[300]}
                                                as="strong"
                                            >
                                                -
                                            </P>{" "}
                                            <P
                                                variant="sm"
                                                fontWeight={400}
                                                color={palette.neutral[300]}
                                                as="strong"
                                            >
                                                {account.organization_number}
                                            </P>
                                        </>
                                    )}
                                </P>
                                <P
                                    variant="xs"
                                    color={palette.neutral[300]}
                                    fontWeight={500}
                                >
                                    {account.account_id}
                                </P>
                            </section>
                            <Icon icon="switch_horizontal" size="small" />
                        </ListItem>
                    );
                })}
            </ListElement>
        </AccountListElement>
    );
}

const ListItem = ({
    $scrollIntoView,
    ...props
}: Parameters<typeof ListItemElement>[0] & { $scrollIntoView?: boolean }) => {
    const elementRef = useRef<HTMLLIElement>(null);

    useEffect(() => {
        if (elementRef.current && props.$highlighted && $scrollIntoView) {
            elementRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start",
            });
        }
    }, [props.$highlighted, $scrollIntoView]);

    return <ListItemElement {...props} ref={elementRef} />;
};

const STICKY_HEADER_HEIGHT = "20px";

const SearchInputElement = styled.input`
      display: flex;
      height: 40px;
      padding: 8px 12px;
      align-items: center;
      gap: 12px;
      align-self: stretch;
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: ${palette.white};
      margin: ${distances.tiny};
      transition: all 0.15s;

      border-radius: ${defaultRadius};
      border: 0px solid ${palette.neutral[400]};
      background: ${palette.darkmode[900]};

      box-shadow: ${shadows.xsmall};

      &::placeholder {
        color: ${placeholderColor};
        opacity: 1;
      }

      &:focus {
        outline: 2px solid ${palette.primary[300]};
        outline-offset: 2px;
        box-shadow: ${focusBoxShadow};
      }
  `;

const ListItemElement = styled.li<{ $highlighted?: boolean }>`
      background: unset;
      border: unset;
      text-align: left;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-inline: ${distances.tiny};
      padding: ${distances.tiny};
      gap: ${distances.tiny};
      color: ${palette.neutral[500]};
      border-radius: 4px;
      outline: 4px solid transparent;
      transition: all 0.15s;
      z-index: 1;
      background: ${({ $highlighted }) =>
          $highlighted ? palette.darkmode[50] : palette.darkmode[600]};
      cursor: pointer;
      section {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        flex: 1;
      }
      &:focus-visible {
        outline-color: ${palette.primary[100]};
        outline-offset: -2px;
        background: ${palette.darkmode[50]};
      }
      &:hover {
        background: ${palette.darkmode[50]};
      }
      `;

const AccountListElement = styled.div`
      display: flex;
      flex-direction: column;
      width: 100%;
    `;
const ListElement = styled.ul`
      display: flex;
      flex-direction: column;
      padding: 0;
      margin: 0;
      overflow: auto;
      max-height: 348px;
      padding-bottom: 4px;
      scroll-padding-top: ${STICKY_HEADER_HEIGHT};
      scrollbar-width: thin;
      scrollbar-color: ${palette.neutral[400]} transparent;
      `;

const StickyListHeaderElement = styled.li`
      position: sticky;
      top: 0;
      display: flex;
      box-sizing: border-box;
      height: ${STICKY_HEADER_HEIGHT};
      padding: 2px 10px;
      align-items: center;
      gap: 6px;
      align-self: stretch;
      border-radius: 24px;
      background: ${palette.darkmode[300]};
      font-size: 12px;
      color: ${palette.neutral[300]};
      margin-inline: ${distances.tiny};
      `;
